import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import SearchAutocomplete from "@/components/SearchAutocomplete";
import MobileSearchSheet from "@/components/MobileSearchSheet";
import { Gem, Bell, Menu, X, Moon, Sun, ShoppingCart, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function Logo() {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
      <div className="relative h-8 w-8 flex items-center justify-center">
        <Gem className="h-7 w-7 text-primary" strokeWidth={1.5} />
      </div>
      <span className="font-display font-extrabold text-xl tracking-tight">
        Jewel<span className="text-primary">tix</span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const { user, logout, becomeSeller } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    if (user) api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, [user]);

  const unread = notifs.filter((n) => !n.read).length;
  const dashPath =
    user?.role === "admin" || user?.role === "employee"
      ? "/admin"
      : user?.role === "seller"
      ? "/seller"
      : "/buyer";

  const toggleDark = () => {
    document.documentElement.classList.toggle("dark");
    setDark((d) => !d);
  };

  return (
    <header className="sticky top-0 z-50 glass-nav border-b border-border">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium ml-4">
          <Link to="/marketplace" className="hover:text-primary transition-colors" data-testid="nav-marketplace">Marketplace</Link>
          <Link to="/visual-search" className="hover:text-primary transition-colors" data-testid="nav-visual-search">Photo Search</Link>
          <Link to="/designers" className="hover:text-primary transition-colors" data-testid="nav-designers">Designers</Link>
          <Link to="/how-it-works" className="hover:text-primary transition-colors">How it Works</Link>
          <Link to="/pricing" className="hover:text-primary transition-colors">Pricing</Link>
          <Link to="/vendor/apply" data-testid="nav-vendor-apply" className="hover:text-primary transition-colors inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            For CAM Vendors
          </Link>
        </nav>

        <SearchAutocomplete className="hidden md:flex flex-1 max-w-md ml-auto" />

        <div className="flex items-center gap-2 ml-auto md:ml-0">
          {/* Mobile search — phones had no way to search the catalogue at
              all (the field above is md-and-up only). */}
          <button
            onClick={() => setSearchOpen(true)}
            className="md:hidden p-2 tap-target flex items-center justify-center rounded-md hover:bg-secondary"
            data-testid="mobile-search-btn"
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </button>

          <button onClick={toggleDark} className="p-2 rounded-md hover:bg-secondary" data-testid="theme-toggle">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <Link to="/cart" data-testid="nav-cart" className="relative p-2 rounded-md hover:bg-secondary" title="Cart">
            <ShoppingCart className="h-4 w-4" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] flex items-center justify-center bg-primary text-primary-foreground rounded-full" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="relative p-2 rounded-md hover:bg-secondary" data-testid="notifications-btn">
                    <Bell className="h-4 w-4" />
                    {unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] flex items-center justify-center bg-primary text-primary-foreground rounded-full">
                        {unread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-sm font-semibold">Notifications</span>
                    {unread > 0 && (
                      <button
                        className="text-xs text-primary"
                        onClick={() => api.post("/notifications/read").then(() => setNotifs((n) => n.map((x) => ({ ...x, read: true }))))}
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <div className="max-h-80 overflow-auto">
                    {notifs.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications</p>}
                    {notifs.slice(0, 10).map((n) => (
                      <div key={n.id} className={`px-3 py-2 border-b border-border last:border-0 ${!n.read ? "bg-primary/5" : ""}`}>
                        <p className="text-sm font-medium">{n.title}</p>
                        <p className="text-xs text-muted-foreground">{n.message}</p>
                      </div>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-1" data-testid="user-menu">
                    <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm">
                      {user.name?.[0]?.toUpperCase()}
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{user.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {(user.role === "buyer" || user.role === "both") && <DropdownMenuItem onClick={() => nav("/buyer")} data-testid="menu-buyer">Buyer Dashboard</DropdownMenuItem>}
                  {(user.role === "seller" || user.role === "both") && <DropdownMenuItem onClick={() => nav("/seller")} data-testid="menu-seller">Designer Studio</DropdownMenuItem>}
                  {(user.role === "admin" || user.role === "employee") && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">Admin Console</DropdownMenuItem>}
                  {user.role === "buyer" && <DropdownMenuItem onClick={async () => { await becomeSeller(); nav("/seller"); }} data-testid="menu-become-seller">Become a seller</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout">Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden md:inline-flex text-sm font-medium px-3 py-2 hover:text-primary" data-testid="nav-login">Log in</Link>
              <Link
                to="/register"
                data-testid="nav-signup"
                className="hidden md:inline-flex text-sm font-medium px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Get Started
              </Link>
            </>
          )}
          <button className="lg:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border bg-background px-4 py-3 flex flex-col gap-2">
          <button
            onClick={() => { setOpen(false); setSearchOpen(true); }}
            data-testid="mobile-nav-search"
            className="flex items-center gap-2 bg-secondary/60 border border-border rounded-full px-4 py-2.5 text-sm text-muted-foreground text-left"
          >
            <Search className="h-4 w-4" /> Search designs, categories…
          </button>
          <Link to="/marketplace" onClick={() => setOpen(false)}>Marketplace</Link>
          <Link to="/visual-search" onClick={() => setOpen(false)}>Photo Search</Link>
          <Link to="/designers" onClick={() => setOpen(false)}>Designers</Link>
          <Link to="/how-it-works" onClick={() => setOpen(false)}>How it Works</Link>
          <Link to="/pricing" onClick={() => setOpen(false)}>Pricing</Link>
          <Link to="/vendor/apply" onClick={() => setOpen(false)} data-testid="mobile-nav-vendor-apply" className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> For CAM Vendors
          </Link>
          {!user && (
            <div className="mt-2 pt-2 border-t border-border flex flex-col gap-2">
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium py-2 hover:text-primary" data-testid="mobile-nav-login">Log in</Link>
              <Link to="/register" onClick={() => setOpen(false)} data-testid="mobile-nav-signup" className="text-sm font-medium px-4 py-2 rounded-md bg-primary text-primary-foreground text-center hover:bg-primary/90 transition-colors">Get Started</Link>
            </div>
          )}
        </div>
      )}
      <MobileSearchSheet open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
